// src/hooks/plan/useBizManagePage.js
import { useState, useMemo, useContext } from 'react'; 
import { AuthContext } from '@context'; 
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import planApi from '@api/planApi';
import { normalizePage } from '@utils/paging';

export default function useBizManagePage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);

  const [filters, setFilters] = useState({ branch: '', bizName: '', bizRegNo: '', pageNum: 1, pageSize: 10 });
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);

  // 🌟 (수정) 심플하게 String 배열(bizMgtNo)로 상태 관리
  const [selectedIds, setSelectedIds] = useState([]); 
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const query = useQuery({ queryKey: ['plan', 'biz', filters], queryFn: () => planApi.listBiz(filters), keepPreviousData: true });

  const page = useMemo(() => {
    const n = normalizePage(query.data);
    if (!n?.rows && query.data) return { rows: query.data.list || query.data.rows || [], total: query.data.total || 0, pageNum: query.data.pageNum || filters.pageNum, pageSize: query.data.pageSize || filters.pageSize };
    return n || { rows: [], total: 0, pageNum: filters.pageNum, pageSize: filters.pageSize };
  }, [query.data, filters.pageNum, filters.pageSize]);

  const invalidateRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['plan', 'visits'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables'] }); 
    queryClient.invalidateQueries({ queryKey: ['plan', 'assignments'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'biz'] });
  };

  const excelUploadMutation = useMutation({ mutationFn: (file) => planApi.uploadBizExcel(file), onSuccess: invalidateRelatedQueries });

  const createBizMutation = useMutation({
    mutationFn: (payload) => planApi.createBiz(payload), 
    onSuccess: () => {
      alert("신규 사업장이 등록/업데이트 되었습니다.");
      setIsCreateModalOpen(false); 
      invalidateRelatedQueries(); 
    }
  });

  const updateBizMutation = useMutation({
    mutationFn: (payload) => planApi.updateBiz(payload),
    onSuccess: () => {
      alert("사업장 정보가 성공적으로 수정되었습니다.");
      setIsEditModalOpen(false);
      setSelectedIds([]); // 수정 완료 후 선택 해제
      invalidateRelatedQueries();
    }
  });

  // 🌟 삭제 Mutation
  const deleteBizMutation = useMutation({
    mutationFn: (ids) => planApi.deleteBizList(ids),
    onSuccess: () => {
      alert("선택한 항목이 성공적으로 삭제되었습니다.");
      setSelectedIds([]);
      invalidateRelatedQueries();
    }
  });

  const handleExcelUpload = async (file) => {
    if (!file) return;
    try {
      const res = await excelUploadMutation.mutateAsync(file);
      const total = res?.totalRows ?? 0;
      const processed = res?.processedRows ?? 0;
      alert(`엑셀 업로드가 완료되었습니다.\n(총 ${total}건 중 ${processed}건 등록/수정)`);
      setIsExcelModalOpen(false); 
    } catch (error) {
      alert(error?.response?.data?.message || error?.message || '엑셀 업로드 중 오류가 발생했습니다.');
    }
  };

  const onSearch = () => { setFilters(prev => ({ ...prev, pageNum: 1 })); setSelectedIds([]); };

  // 🌟 체크박스 토글 함수 
  const toggleSelectRow = (bizMgtNo, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, bizMgtNo]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== bizMgtNo));
    }
  };
  const isSelected = (bizMgtNo) => selectedIds.includes(bizMgtNo);
  const anyChecked = selectedIds.length > 0;

  // 단일 선택만 정보 수정 가능하게 유효성 검사
  const handleEditClick = () => {
    if (selectedIds.length !== 1) {
      alert("정보를 수정하려면 사업장을 정확히 1개만 선택해주세요.");
      return;
    }
    setIsEditModalOpen(true);
  };

  // 🌟 삭제 클릭 핸들러
  const handleDeleteClick = () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`선택한 ${selectedIds.length}개 사업장을 목록에서 삭제하시겠습니까?`)) {
      deleteBizMutation.mutate(selectedIds);
    }
  };

  const handleCreateBiz = async (payload) => await createBizMutation.mutateAsync(payload);
  const handleUpdateBiz = async (payload) => await updateBizMutation.mutateAsync(payload);

  return {
    filters, setFilters, page, onSearch,
    isExcelModalOpen, setIsExcelModalOpen, handleExcelUpload, isUploading: excelUploadMutation.isPending,
    
    selectedIds, toggleSelectRow, isSelected, anyChecked,
    handleEditClick, handleUpdateBiz, isEditModalOpen, setIsEditModalOpen, isUpdating: updateBizMutation.isPending,
    handleDeleteClick, isDeleting: deleteBizMutation.isPending,
    
    isCreateModalOpen, setIsCreateModalOpen, handleCreateBiz, isCreating: createBizMutation.isPending,
    auth
  };
}